// AbracadaAppEntry - Page d'entrée pour abracada.app
// L'utilisateur arrive directement sur le Cockpit Particulier
// Style ChatGPT/Claude : connexion/inscription intégrée

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Lock, Eye, EyeOff, ArrowRight, User, X, Wand2, 
  Briefcase, Sparkles, Shield, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { API_URL } from '../../config/api';
import { useCurrentUser } from '../../hooks/useCurrentUser';

// Couleurs
const C = {
  navy: '#1E3A5F',
  orange: '#F97316',
  green: '#22C55E',
  cream: '#FFFBF7',
};

export default function AbracadaAppEntry() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useCurrentUser();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' ou 'register'
  
  // Si déjà connecté, rediriger vers le cockpit
  useEffect(() => {
    if (isLoggedIn && user) {
      navigate('/cockpit-particulier');
    }
  }, [isLoggedIn, user, navigate]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: `linear-gradient(135deg, ${C.navy} 0%, #0F2744 100%)`,
      display: 'flex',
      flexDirection: 'column',
    }}>
      
      {/* Header minimaliste */}
      <header style={{
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Wand2 size={22} color="#fff" />
          </div>
          <span style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: 22,
            color: '#fff',
          }}>
            Abracada<span style={{ color: C.orange }}>.app</span>
          </span>
        </div>

        {/* Boutons connexion / inscription */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 10,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            data-testid="login-btn"
          >
            Connexion
          </button>
          <button
            onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
            style={{
              padding: '10px 20px',
              background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
              border: 'none',
              borderRadius: 10,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
            }}
            data-testid="register-btn"
          >
            S'inscrire gratuitement
          </button>
        </div>
      </header>

      {/* Contenu principal - Aperçu Cockpit */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        textAlign: 'center',
      }}>
        
        {/* Titre principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(36px, 6vw, 56px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.1,
            marginBottom: 20,
          }}>
            Ton assistant personnel<br />
            <span style={{ color: C.orange }}>magique</span>
          </h1>
          
          <p style={{
            fontSize: 'clamp(16px, 2vw, 20px)',
            color: 'rgba(255,255,255,0.7)',
            maxWidth: 600,
            margin: '0 auto 40px',
            lineHeight: 1.6,
          }}>
            Gère ton quotidien, tes annonces, tes mails, ton agenda... 
            avec l'aide de ton Super Assistant IA.
          </p>
        </motion.div>

        {/* Aperçu du Cockpit (image ou mockup) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            width: '100%',
            maxWidth: 900,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 24,
            padding: 24,
            marginBottom: 40,
          }}
        >
          {/* Simulation d'interface Cockpit */}
          <div style={{
            background: '#0a1628',
            borderRadius: 16,
            padding: 20,
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Barre de chat simulée */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16,
            }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Sparkles size={32} color="#fff" />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16 }}>
                Comment puis-je t'aider aujourd'hui ?
              </p>
            </div>
            
            {/* Input simulé */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12,
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <span style={{ color: 'rgba(255,255,255,0.4)', flex: 1 }}>
                Écris ton message...
              </span>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: C.orange,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ArrowRight size={18} color="#fff" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}
        >
          <button
            onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '18px 36px',
              background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
              border: 'none',
              borderRadius: 14,
              color: '#fff',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer',
              boxShadow: '0 8px 30px rgba(249, 115, 22, 0.4)',
              transition: 'all 0.2s ease',
            }}
            data-testid="main-cta-btn"
          >
            <Wand2 size={20} />
            Commencer gratuitement
            <ArrowRight size={18} />
          </button>
          
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            Inscription gratuite • Pas de carte bancaire requise
          </p>
        </motion.div>

        {/* Lien Espace Pro */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{ marginTop: 48 }}
        >
          <button
            onClick={() => navigate('/cockpit-pro-preview')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 24px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 12,
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.1)';
              e.target.style.borderColor = C.orange;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.05)';
              e.target.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
            data-testid="espace-pro-btn"
          >
            <Briefcase size={18} />
            Tu es professionnel ? Découvre l'Espace Pro
            <ArrowRight size={16} />
          </button>
        </motion.div>
      </main>

      {/* Modal Auth */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
}

// Composant Modal d'authentification
function AuthModal({ isOpen, onClose, mode, setMode }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    
    try {
      const loginUrl = `${API_URL}/api/client/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('buyer_token', data.token);
        localStorage.setItem('buyer_user', JSON.stringify(data.user));
        localStorage.setItem('ecosystem_token', data.token);
        localStorage.setItem('ecosystem_user', JSON.stringify(data.user));
        toast.success(`Bienvenue ${data.user.name} !`);
        onClose();
        navigate('/cockpit-particulier');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (!acceptTerms) {
      toast.error('Veuillez accepter les conditions d\'utilisation');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/client/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, user_type: 'particulier' }),
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('buyer_token', data.token);
        localStorage.setItem('buyer_user', JSON.stringify(data.user));
        localStorage.setItem('ecosystem_token', data.token);
        localStorage.setItem('ecosystem_user', JSON.stringify(data.user));
        toast.success(`Bienvenue ${data.user.name} ! Ton compte a été créé.`);
        onClose();
        navigate('/cockpit-particulier');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20,
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 24,
            width: '100%',
            maxWidth: 440,
            padding: 32,
            position: 'relative',
          }}
        >
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: '#F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            data-testid="close-auth-modal"
          >
            <X size={18} color="#64748B" />
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Wand2 size={28} color="#fff" />
            </div>
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 24,
              fontWeight: 700,
              color: C.navy,
              margin: 0,
              marginBottom: 8,
            }}>
              {mode === 'login' ? 'Content de te revoir !' : 'Crée ton compte'}
            </h2>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
              {mode === 'login' 
                ? 'Connecte-toi pour accéder à ton Cockpit' 
                : 'Rejoins la communauté Abracada'}
            </p>
          </div>

          {/* Formulaire */}
          <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
            {mode === 'register' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 6 }}>
                  Ton prénom
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jean"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 44px',
                      border: '1.5px solid #E2E8F0',
                      borderRadius: 12,
                      fontSize: 15,
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.orange}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 6 }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jean@exemple.com"
                  style={{
                    width: '100%',
                    padding: '14px 14px 14px 44px',
                    border: '1.5px solid #E2E8F0',
                    borderRadius: 12,
                    fontSize: 15,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={(e) => e.target.style.borderColor = C.orange}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            <div style={{ marginBottom: mode === 'register' ? 16 : 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 6 }}>
                Mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '14px 44px 14px 44px',
                    border: '1.5px solid #E2E8F0',
                    borderRadius: 12,
                    fontSize: 15,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={(e) => e.target.style.borderColor = C.orange}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={18} color="#94A3B8" /> : <Eye size={18} color="#94A3B8" />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    style={{ marginTop: 3 }}
                    data-testid="accept-terms-checkbox"
                  />
                  <span style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                    J'accepte les <a href="/cgu" style={{ color: C.orange }}>conditions d'utilisation</a> et la <a href="/confidentialite" style={{ color: C.orange }}>politique de confidentialité</a>
                  </span>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                background: loading ? '#94A3B8' : `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
                border: 'none',
                borderRadius: 12,
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: loading ? 'none' : '0 4px 15px rgba(249, 115, 22, 0.3)',
              }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                'Chargement...'
              ) : mode === 'login' ? (
                <>Se connecter <ArrowRight size={18} /></>
              ) : (
                <>Créer mon compte <ArrowRight size={18} /></>
              )}
            </button>
          </form>

          {/* Switch mode */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <p style={{ color: '#64748B', fontSize: 14 }}>
              {mode === 'login' ? (
                <>
                  Pas encore de compte ?{' '}
                  <button
                    onClick={() => setMode('register')}
                    style={{ color: C.orange, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Inscris-toi
                  </button>
                </>
              ) : (
                <>
                  Déjà un compte ?{' '}
                  <button
                    onClick={() => setMode('login')}
                    style={{ color: C.orange, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Connecte-toi
                  </button>
                </>
              )}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
